import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ExternalLink, Play, ChevronLeft, ChevronRight, FileText, Video, Image as ImageIcon, Download, Eye, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingImage, setViewingImage] = useState(null);

  const bgColor = '#1a2332';
  const accentColor = '#d4af37';

  useEffect(() => {
    loadGalleryData();
  }, []);

  const loadGalleryData = async () => {
    try {
      const [imagesRes, articlesRes, sessionsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/gallery/images`),
        fetch(`${BACKEND_URL}/api/gallery/articles`),
        fetch(`${BACKEND_URL}/api/gallery/sessions`)
      ]);

      setImages(await imagesRes.json());
      setArticles(await articlesRes.json());
      setSessions(await sessionsRes.json());
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || 'image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(url, '_blank');
    }
  };

  // Data is now loaded from backend via useEffect

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-white text-xl">Loading gallery...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEOHead 
        title="Gallery | Venkat Swaroop - Articles, Speaking Sessions & Photos"
        description="Explore Venkat Swaroop's professional journey through contributed articles, speaking sessions, and memorable moments from his career in talent acquisition and HR leadership."
        keywords="Venkat Swaroop gallery, speaking engagements, HR articles, talent acquisition photos, leadership events, professional gallery"
        url="https://venkatswaroop.in/gallery"
      />
      <Header />

      <div className="container mx-auto px-6 pt-24 pb-12">
        {/* Hero Section */}
        <div className="mb-16 text-center">
          <h1 className="mb-4 font-serif text-5xl font-bold text-white">Gallery</h1>
          <div className="mx-auto h-1 w-24 rounded mb-6" style={{ backgroundColor: accentColor }}></div>
          <p className="mx-auto max-w-3xl text-lg text-white/90">
            A collection of my professional journey—articles, speaking engagements, and memorable moments
          </p>
        </div>

        {/* Section 1: Articles Contributed */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="h-8 w-8 text-amber-400" />
            <h2 className="font-serif text-3xl font-bold text-white">Articles Contributed</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Card 
                key={article.id}
                className="border-none bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all group"
              >
                <CardContent className="p-6">
                  <Badge 
                    variant="outline" 
                    className="mb-3 text-white border-white/30"
                  >
                    {article.publication}
                  </Badge>
                  <h3 className="font-serif text-xl font-bold text-white mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-3">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-white/50 text-xs">{article.date}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(article.url, '_blank')}
                      className="text-amber-400 hover:text-amber-300 hover:bg-white/10"
                    >
                      Read Article
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {articles.length === 0 && (
              <div className="col-span-full text-center py-12 text-white/60">
                No articles yet. Add articles from the admin panel.
              </div>
            )}
          </div>
        </section>

        {/* Section 2: Speaking Sessions */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <Video className="h-8 w-8 text-amber-400" />
            <h2 className="font-serif text-3xl font-bold text-white">Speaking Sessions</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <Card 
                key={session.id}
                className="border-none bg-white/5 backdrop-blur-sm overflow-hidden hover:bg-white/10 transition-all group"
              >
                <div className="relative">
                  <img 
                    src={session.thumbnail} 
                    alt={session.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1540317580384-e5d43616e5a9?w=800&h=450&fit=crop";
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="h-16 w-16 text-white" />
                  </div>
                  <Badge 
                    className="absolute top-4 right-4"
                    style={{ backgroundColor: accentColor, color: bgColor }}
                  >
                    {session.platform}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-serif text-xl font-bold text-white mb-3">
                    {session.title}
                  </h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-3">
                    {session.description}
                  </p>
                  <Button
                    onClick={() => window.open(session.url, '_blank')}
                    className="w-full"
                    style={{ backgroundColor: accentColor, color: bgColor }}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Watch Session
                  </Button>
                </CardContent>
              </Card>
            ))}
            {sessions.length === 0 && (
              <div className="col-span-full text-center py-12 text-white/60">
                No sessions yet. Add speaking sessions from the admin panel.
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Images */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <ImageIcon className="h-8 w-8 text-amber-400" />
            <h2 className="font-serif text-3xl font-bold text-white">Professional Moments</h2>
          </div>

          {images.length > 0 ? (
            <>
              {/* Carousel */}
              <Card className="border-none bg-white/5 backdrop-blur-sm mb-12 overflow-hidden">
                <div className="relative">
                  <img 
                    src={images[currentImageIndex]?.url}
                    alt={images[currentImageIndex]?.title}
                    className="w-full h-96 object-cover cursor-pointer"
                    onClick={() => setViewingImage(images[currentImageIndex])}
                  />
                  
                  {/* Carousel Controls */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => setViewingImage(images[currentImageIndex])}
                      className="bg-black/50 hover:bg-black/70 text-white"
                    >
                      <ZoomIn className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDownload(images[currentImageIndex]?.url, images[currentImageIndex]?.title)}
                      className="bg-black/50 hover:bg-black/70 text-white"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>

                  {/* Caption */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <p className="text-white text-lg font-semibold">
                      {images[currentImageIndex]?.title}
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      {images[currentImageIndex]?.caption}
                    </p>
                    <p className="text-white/60 text-sm mt-1">
                      Image {currentImageIndex + 1} of {images.length}
                    </p>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <div className="text-center py-12 text-white/60 mb-12">
              No images yet. Add images from the admin panel.
            </div>
          )}

          {/* Grid Gallery */}
          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={`relative overflow-hidden rounded-lg cursor-pointer group ${
                    currentImageIndex === index ? 'ring-2 ring-amber-400' : ''
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-48 object-cover transition-transform group-hover:scale-110"
                    onClick={() => setCurrentImageIndex(index)}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all flex flex-col items-center justify-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => setViewingImage(image)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 hover:bg-white/30 text-white"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(image.url, image.title);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 hover:bg-white/30 text-white"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Image Viewer Modal */}
          {viewingImage && (
            <div 
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setViewingImage(null)}
            >
              <div className="relative max-w-6xl w-full">
                <img 
                  src={viewingImage.url} 
                  alt={viewingImage.title}
                  className="w-full h-auto max-h-[90vh] object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-white text-xl font-bold">{viewingImage.title}</h3>
                  <p className="text-white/80">{viewingImage.caption}</p>
                </div>
                <div className="absolute top-4 right-4 flex gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(viewingImage.url, viewingImage.title);
                    }}
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => setViewingImage(null)}
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default GalleryPage;
